import {
  ArrowRight,
  Code2,
  Gamepad2,
  Lightbulb,
  ListTodo,
  PenTool,
  Sparkles,
  Trophy,
  UsersRound,
} from "lucide-react";
import { Link } from "react-router-dom";

const futureIdeas = [
  {
    icon: Sparkles,
    title: "Karakter és mozdulatok",
    body: "Új animációk, támadások és még látványosabb Zsombzilla-pillanatok.",
  },
  {
    icon: Gamepad2,
    title: "Pályák és ellenfelek",
    body: "További helyszínek, járművek és játékötletek várnak a sorukra.",
  },
  {
    icon: Trophy,
    title: "Online játékvilág",
    body: "A profilok, mentett eredmények és toplista finomítása, majd új közösségi kihívások.",
  },
];

export function AboutPage() {
  return (
    <div className="about-page">
      <section className="about-hero" aria-labelledby="about-title">
        <div className="about-hero__image" aria-hidden="true">
          <img
            src="/assets/about/zsombi-figures-original.jpg"
            alt=""
            className="about-hero__photo"
            fetchPriority="high"
          />
          <img
            src="/assets/about/zsombi-figures-grade.jpg"
            alt=""
            className="about-hero__photo about-hero__photo--grade"
          />
        </div>
        <div className="about-hero__shade" aria-hidden="true" />
        <div className="content-frame about-hero__content">
          <p className="eyebrow">
            <UsersRound aria-hidden="true" size={16} /> Az alkotók
          </p>
          <h1 id="about-title">Zsombi ötlete. Apa kódja. Közös játék.</h1>
          <p>
            A Zsombzilla egy apa-fia projekt. Zsombi ma kilencéves, de a Godzilla-mániája már
            nagyjából hatéves korában elkezdődött. Figurákból, rajzokból és közös ötletekből
            építjük a saját kaiju-játékunkat.
          </p>
          <a className="button button--primary" href="#innen-indult">
            A történetünk <ArrowRight aria-hidden="true" size={19} />
          </a>
        </div>
      </section>

      <section className="memory-section" id="innen-indult" aria-labelledby="memory-title">
        <div className="content-frame memory-section__layout">
          <div className="memory-section__copy">
            <p className="section-kicker">Körülbelül hatévesen kezdődött</p>
            <h2 id="memory-title">Amikor már a tortán is Godzilla állt</h2>
            <p>
              A rajongás gyorsan több lett néhány megnézett jelenetnél. A hatodik
              születésnapjára Zsombi Godzilla-tortát kért, rajta egy várossal, egy hatalmas
              szörnnyel és természetesen atomlézerrel.
            </p>
            <p>
              Ezután minden adandó alkalomra Godzilla-figurák kerültek a kívánságlistára. A
              figurák nem a polcon maradtak: tankokkal, helikopterekkel és saját történetekkel
              újra meg újra benépesítették a szoba padlóját.
            </p>
          </div>

          <figure className="story-photo story-photo--portrait">
            <div className="photo-treatment__visual">
              <img
                src="/assets/about/godzilla-cake-original.jpg"
                alt="Zsombi a hatodik születésnapjára készített Godzilla-tortával"
                loading="lazy"
              />
              <img
                src="/assets/about/godzilla-cake-grade.jpg"
                alt=""
                aria-hidden="true"
                className="photo-treatment__grade"
                loading="lazy"
              />
            </div>
            <figcaption>A hatodik születésnapi kívánság: Godzilla, város és atomlézer.</figcaption>
          </figure>
        </div>
      </section>

      <section className="origin-section" aria-labelledby="origin-title">
        <div className="content-frame">
          <div className="origin-section__intro">
            <p className="section-kicker">A figuráktól a saját játékig</p>
            <h2 id="origin-title">Az ötletek először papírra kerültek</h2>
            <p>
              A közös játékokból egy saját szörny született: a hosszú farkú, lila tüskékkel
              világító Zsombzilla. Az első karakterrajz megadta a játék főhősét és színeit, a
              következő tervek pedig már azt is megmutatták, hogyan mozogjon és támadjon mobilon.
            </p>
          </div>

          <div className="design-gallery">
            <figure className="design-art design-art--character">
              <img
                src="/assets/original-zsombzilla-drawing.jpg"
                alt="Zsombi eredeti, kézzel rajzolt Zsombzilla karaktere hosszú farokkal és lila tüskékkel"
                loading="lazy"
              />
              <figcaption>Az eredeti Zsombzilla-rajz, amelyből a játék főhőse megszületett.</figcaption>
            </figure>

            <figure className="design-art design-art--controls">
              <img
                src="/assets/about/mobile-controls-sketch.jpg"
                alt="Zsombi kézzel rajzolt játékterve joystickkal, védekezés, dobbantás, farokcsapás és lézer gombokkal"
                loading="lazy"
              />
              <figcaption>A mobilirányítás első terve: joystick és négy saját akciógomb.</figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section className="workshop-section" aria-labelledby="workshop-title">
        <div className="content-frame">
          <figure className="workshop-photo">
            <img
              src="/assets/about/design-desk-photo.jpg"
              alt="Zsombi és az édesapja a közös tervezőasztalnál rajzokkal és a készülő játékkal"
              loading="lazy"
            />
            <figcaption>
              A közös tervezőasztal: Zsombi rajzol, Apa pedig működő játékká alakítja az ötleteket.
            </figcaption>
          </figure>

          <div className="workshop-section__layout">
            <figure className="workshop-art">
              <img
                src="/assets/gameplay-preview.png"
                alt="A Zsombzilla játék működő képernyője tankokkal, helikopterrel és mobilvezérléssel"
                loading="lazy"
              />
              <figcaption>
                A rajzokból működő játék lett, amelyet együtt próbálunk és alakítunk.
              </figcaption>
            </figure>

            <div className="workshop-section__copy">
              <p className="section-kicker">Apa-fia alkotóműhely</p>
              <h2 id="workshop-title">Zsombi tervezi. Apa megvalósítja Codexszel.</h2>
              <p>
                A játék minden fejlesztési köre beszélgetéssel, rajzzal és közös próbával indul.
                Zsombi megmondja, mitől lesz igazán izgalmas a játék, Apa pedig segít működő kóddá
                alakítani az ötleteket a Codex segítségével. Aztán együtt kipróbáljuk, mi működik,
                és jöhet a következő rajz vagy fejlesztés.
              </p>

              <div className="role-list" aria-label="Alkotói szerepek">
                <div>
                  <PenTool aria-hidden="true" size={23} />
                  <span>
                    <strong>Zsombi</strong>
                    <small>Karakterek, támadások, pályák, kontrollok és játékötletek.</small>
                  </span>
                </div>
                <div>
                  <Code2 aria-hidden="true" size={23} />
                  <span>
                    <strong>Apa</strong>
                    <small>
                      Programozás és technikai megvalósítás a Codex segítségével, valamint webes
                      publikálás.
                    </small>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section content-frame" aria-labelledby="future-title">
        <div className="section-heading">
          <div>
            <p className="section-kicker">A történet folytatódik</p>
            <h2 id="future-title">Rengeteg ötletünk van még</h2>
          </div>
          <span className="section-index">Folyamatos fejlesztés</span>
        </div>

        <div className="future-grid">
          {futureIdeas.map(({ icon: Icon, title, body }) => (
            <article className="future-card" key={title}>
              <Icon aria-hidden="true" size={26} />
              <span className="future-card__status">Backlog</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>

        <div className="backlog-placeholder" aria-label="Nyilvános backlog helyőrző">
          <ListTodo aria-hidden="true" size={25} />
          <div>
            <strong>PUBLIC BACKLOG // HAMAROSAN</strong>
            <p>
              A részletes fejlesztési listát később ezen az oldalon is követhetővé tesszük.
              Addig minden itt látható elem irány, nem megjelenési ígéret.
            </p>
          </div>
        </div>
      </section>

      <section className="about-cta" aria-labelledby="about-cta-title">
        <div className="content-frame about-cta__inner">
          <Lightbulb aria-hidden="true" size={32} />
          <div>
            <p className="section-kicker">A következő ötlet már készül</p>
            <h2 id="about-cta-title">Nézd meg, hol tart most Zsombzilla</h2>
          </div>
          <Link className="button button--primary" to="/play">
            <Gamepad2 aria-hidden="true" size={20} /> Játék indítása
          </Link>
        </div>
      </section>
    </div>
  );
}
